// WS-17: camera bounds (resort.json -> camera.bounds). The guest can orbit, zoom and pan freely inside the resort,
// but can never fly so far out that the world's edges show (the tree disc, the far landscape's rim), and never
// under the terrain. Engine-generic; absent config = the old free camera. Schema: skill references/rendering.md
// "Camera bounds".
//   maxDistance / minDistance  orbit distance (m) from the target
//   minElevation / maxElevation  camera elevation above the horizon at the target (degrees)
//   pan: { margin, zones, places, polygon, overshoot, stiffness }  the target stays inside the convex hull of the
//        core extent, every zone and every place anchor (each grown by `margin` m), or inside `polygon` if given.
//        Soft clamp: past the hull the target is pulled back with `stiffness` (1/s) and never gets more than
//        `overshoot` m outside, so panning into the edge feels like a rubber band, not a wall (no jitter).
//        `activities: true`: also along every ride's route and walked trail (lazy/lanes.js, `lineMargin` m either side).
//   groundClearance  minimum camera height above the terrain under it (m)
//   treeMargin  with a far landscape, the camera stays this far inside the real-tree radius (m; false = off), so it
//        never looks back at the forest's outer edge; the pan hull still wins (every place stays reachable)
// Flights (fly-to, tour, home, arrow pans) are clamped at their destination; the race camera runs free.
// WS-32 (round 6, "endless zoom-out from some angles"): on top of the soft limits, apply() enforces HARD limits every
// frame on every camera path (wheel zoom, pinch, grab orbit, flights, tour, place focus): orbit distance <=
// maxDistance, target never more than `overshoot` m outside the hull, camera inside the circle. Only the race camera
// (free) is exempt. hardOk(pos, t) lets a writer that trims its own steps (grab orbit) check them first.
// WS-43 (user, 2026-10-12: "moving around is very slow", "I can zoom extremely high up", "at the ice karting it gets
// stuck and doesn't let me rotate"): the target is the ground point at the screen centre, always. Every frame
// (outside flights, the race camera and an exact setView pose) seat() slides it along the view ray onto the terrain
// (no visual change), so the orbit distance is the real one and maxDistance also caps the camera's height. reach()
// is the ground distance along the view; a pose that looks at ground further than `viewReach` (1.15) x maxDistance
// glides back along the view (soft, like the rubber band). legal() = hardOk + that reach rule, for writers that trim
// their own steps. createGrabPan (below) replaces MapControls' pan: the grabbed point stays under the cursor.
import * as THREE from 'three/webgpu'; // WS-77: the WebGPU build (core classes only)
import { stretchT } from './lines/measure.js'; // VIS-219: the nearest point of a stretch, the shared sum
import { GRAZE, MOVE_K, PLANE_K, clampStep, flyRate, freeLook, glideSpeed, heightAbove, lookRate, missDist, orbitRate, planeRange, turnRate, twoFingers } from './navmath.js'; // map controls: rates set by the height

function hull(points) { // Andrew's monotone chain, counter-clockwise in (x, z); returns [[x, z], ...]
  const p = points.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (p.length < 3) return p;
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lo = [], up = [];
  for (const q of p) { while (lo.length >= 2 && cross(lo[lo.length - 2], lo[lo.length - 1], q) <= 0) lo.pop(); lo.push(q); }
  for (let i = p.length - 1; i >= 0; i--) { const q = p[i]; while (up.length >= 2 && cross(up[up.length - 2], up[up.length - 1], q) <= 0) up.pop(); up.push(q); }
  return lo.slice(0, -1).concat(up.slice(0, -1));
}
const grow = ([x, z], m) => (m ? [[x - m, z - m], [x + m, z - m], [x + m, z + m], [x - m, z + m]] : [[x, z]]);

/** Nearest point of a convex polygon to (x, z), and the signed distance (< 0 inside). */
function nearest(poly, x, z) {
  let inside = true, best = Infinity, bx = x, bz = z;
  for (let i = 0; i < poly.length; i++) {
    const [ax, az] = poly[i], [cx, cz] = poly[(i + 1) % poly.length];
    const vx = cx - ax, vz = cz - az;
    if (vx * (z - az) - vz * (x - ax) < 0) inside = false; // right of a ccw edge = outside
    const t = stretchT(x, z, ax, az, cx, cz);
    const px = ax + vx * t, pz = az + vz * t, d = Math.hypot(px - x, pz - z);
    if (d < best) { best = d; bx = px; bz = pz; }
  }
  return { x: inside ? x : bx, z: inside ? z : bz, d: inside ? -best : best };
}

/**
 * opts: { camera, controls, cfg (resort.camera.bounds), extent, zones ([{ polygon|polygons }]), anchors ([[x, z]]),
 *         lanes (pan.activities: lazy/lanes.js corridors, or null),
 *         heightAt(x, z) (scene y of the ground) }
 * Returns null when cfg is absent, else { apply(dt, free), hull, enabled, contains(x, z), info() }.
 */
export function createCameraBounds({ camera, controls, cfg, extent, zones = [], anchors = [], lanes = null, heightAt, circle: circleIn = null }) {
  if (!cfg) return null;
  const P = cfg.pan || {};
  if (cfg.maxDistance) controls.maxDistance = cfg.maxDistance;
  if (cfg.minDistance) controls.minDistance = cfg.minDistance;
  if (cfg.minElevation != null) controls.maxPolarAngle = THREE.MathUtils.degToRad(90 - cfg.minElevation);
  if (cfg.maxElevation != null) controls.minPolarAngle = THREE.MathUtils.degToRad(90 - cfg.maxElevation);
  let poly;
  if (Array.isArray(P.polygon) && P.polygon.length > 2) poly = hull(P.polygon);
  else {
    const m = P.margin ?? 0, pts = [[extent.minX - m, extent.minZ - m], [extent.maxX + m, extent.minZ - m], [extent.maxX + m, extent.maxZ + m], [extent.minX - m, extent.maxZ + m]];
    if (P.zones !== false) for (const z of zones) for (const r of z.polygons || (z.polygon ? [z.polygon] : [])) for (const q of r) pts.push(...grow(q, P.zoneMargin ?? 40));
    if (P.places !== false) for (const a of anchors) pts.push(...grow(a, P.placeMargin ?? 60));
    poly = hull(pts);
  }
  const region = (x, z) => (lanes ? lanes.near(nearest(poly, x, z), x, z) : nearest(poly, x, z)); // the hull, or a corridor when nearer
  const over = P.overshoot ?? 40, stiff = P.stiffness ?? 7, clear = cfg.groundClearance ?? 4;
  // camera circle: { c: [x, z], r } — the caller derives it from the real-tree radius (cfg.treeMargin m inside it),
  // never smaller than the pan hull (+60 m), so every place stays reachable
  let circle = null;
  const fit = () => {
    if (!circleIn) return;
    const far = Math.max(...poly.map(([x, z]) => Math.hypot(x - circleIn.c[0], z - circleIn.c[1])), lanes?.far(circleIn.c) ?? 0);
    api.circle = circle = { c: circleIn.c, r: Math.max(circleIn.r, far + 60) };
  };
  // VIS-265 Film: `loose` = none of the visitor's limits (the pan hull, the distance, the circle, the reach); only the
  // target seated and the camera kept `clearance` over the ground (Film sets both while it has the map)
  const api = { hull: poly, circle, enabled: true, loose: false, contains: (x, z) => region(x, z).d <= 0, refit: () => { lanes?.refresh?.(); fit(); } }; // refit: the activities' lines again (main.js: a path or a ride laid or moved)
  fit();
  const d = new THREE.Vector3(), e = new THREE.Vector3();
  // Shorten the orbit offset (pos - t) so that pos stays inside the camera circle; returns true if it changed pos.
  const circleLimit = (pos, t) => {
    if (!circle) return false;
    const cx = circle.c[0], cz = circle.c[1], px = pos.x - cx, pz = pos.z - cz;
    if (px * px + pz * pz <= circle.r * circle.r) return false;
    e.copy(pos).sub(t); const len = e.length(); if (len < 1e-6) return false;
    const dx = e.x / len, dz = e.z / len, tx = t.x - cx, tz = t.z - cz, a = dx * dx + dz * dz, b = 2 * (tx * dx + tz * dz), c = tx * tx + tz * tz - circle.r * circle.r;
    const disc = b * b - 4 * a * c;
    if (!(a > 1e-6 && c < 0 && disc > 0)) return false;
    const s = Math.max(controls.minDistance, (-b + Math.sqrt(disc)) / (2 * a));
    if (s >= len) return false;
    pos.copy(t).addScaledVector(e, s / len);
    return true;
  };
  // A flight (fly-to, tour, arrow-key pan, home) is clamped once, at its destination: the target onto the hull, the
  // camera into the circle, so it lands inside the limits instead of being pulled back after landing (no jump).
  api.clampFlight = (f) => {
    if (!f || f.wsBounded) return;
    f.wsBounded = true;
    const n = region(f.toT.x, f.toT.z);
    if (n.d > 0) { d.set(n.x - f.toT.x, 0, n.z - f.toT.z); f.toT.add(d); f.toP.add(d); }
    circleLimit(f.toP, f.toT);
    if (f.sTo) f.sTo.setFromVector3(e.copy(f.toP).sub(f.toT)); // main.js stepFlight caches the end orbit on its first step
  };
  // WS-32: the hard limits (see the header). Returns true if it moved the camera or the target.
  const hard = (pos, t) => {
    let moved = false;
    const n = region(t.x, t.z);
    if (n.d > over + 0.01) { const k = (n.d - over) / n.d; d.set((n.x - t.x) * k, 0, (n.z - t.z) * k); t.add(d); pos.add(d); moved = true; }
    e.copy(pos).sub(t);
    const len = e.length(), maxD = controls.maxDistance;
    if (len > maxD + 0.01) { pos.copy(t).addScaledVector(e, maxD / len); moved = true; }
    if (circleLimit(pos, t)) moved = true;
    return moved;
  };
  api.hardOk = (pos, t) => api.loose || region(t.x, t.z).d <= over + 0.01 && pos.distanceTo(t) <= controls.maxDistance + 0.01 && api.circleOk(pos);
  // ---- WS-43 (begin): the target on the ground, the view's reach (see the header) ----
  const FAR = cfg.viewReach ?? 1.15, rd = new THREE.Vector3(), fwd = new THREE.Vector3();
  const rayGround = (o, dir, limit) => { // distance along the ray to the terrain (Infinity: none within `limit`)
    const above = (s) => o.y + dir.y * s - heightAt(o.x + dir.x * s, o.z + dir.z * s);
    if (above(0) <= 0) return 0;
    for (let prev = 0, s = 1; ; prev = s, s = Math.min(limit, s * 1.08 + 1)) {
      if (above(s) <= 0) { let a = prev, b = s; for (let i = 0; i < 18; i++) { const m = (a + b) / 2; if (above(m) <= 0) b = m; else a = m; } return b; }
      if (s >= limit) return Infinity;
    }
  };
  api.maxReach = () => (api.loose ? Infinity : controls.maxDistance * FAR);
  /** Ground distance along the view ray pos -> t (Infinity when it meets no ground within 8 x maxDistance). */
  api.reach = (pos, t) => {
    if (!heightAt) return 0;
    rd.copy(t).sub(pos); const L = rd.length(); if (L < 1e-6) return Infinity;
    return rayGround(pos, rd.divideScalar(L), controls.maxDistance * 8);
  };
  /** Slide t along the view ray onto the ground (orbit distance clamped to the controls' range); returns the reach. */
  api.seat = (pos, t) => {
    const s = api.reach(pos, t);
    if (!Number.isFinite(s) || !heightAt) return s;
    const L = pos.distanceTo(t), want = THREE.MathUtils.clamp(s, controls.minDistance, controls.maxDistance);
    if (Math.abs(want - L) > 0.05) t.copy(pos).addScaledVector(rd, want); // rd = the unit view ray from reach()
    return s;
  };
  api.legal = (pos, t) => { if (!api.hardOk(pos, t)) return false; const s = api.reach(pos, t); return !(Number.isFinite(s) && s > api.maxReach()); };
  api.exact = false; // setView: an exact (camera-matched) pose is kept as it is until the next user input
  const unexact = () => { api.exact = false; };
  for (const ev of ['pointerdown', 'wheel', 'keydown']) addEventListener(ev, unexact, { capture: true, passive: true });
  // ---- WS-43 (end)
  api.apply = (dt, free = false, flight = null) => {
    if (!api.enabled) return;
    const t = controls.target, L = api.loose;
    if (flight && !L) api.clampFlight(flight);
    let moved = false;
    let reach = NaN; // WS-43: seat the target (no visual change), remember how far the view reaches
    if (!free && !flight && !api.exact && heightAt) reach = api.seat(camera.position, t);
    if (!free && !flight && !api.grabbing && !api.panning && !L) { // during a grab-orbit drag the pivot stays pinned, a pan drag has its own soft edge (WS-43); the band relaxes after release
      const n = region(t.x, t.z);
      if (n.d > 0.01) {
        // rubber band: relax towards the hull, but never more than `over` metres outside it
        const h = Math.min(dt, 0.1), k = 1 - Math.exp(-stiff * h);
        const step = Math.min(n.d, Math.max(n.d * k, 30 * h)); // exponential far out, at least 30 m/s near the edge
        let nx = t.x + ((n.x - t.x) / n.d) * step, nz = t.z + ((n.z - t.z) / n.d) * step;
        const out = Math.hypot(nx - n.x, nz - n.z);
        if (out > over) { nx = n.x + ((nx - n.x) / out) * over; nz = n.z + ((nz - n.z) / out) * over; }
        d.set(nx - t.x, 0, nz - t.z);
        t.add(d); camera.position.add(d);
        moved = true;
      }
    }
    // the camera itself stays over the detailed world (inside the real-tree disc): the orbit distance shortens
    // along the current view direction instead of the view sliding (continuous while zooming, so no jump)
    if (!free && !flight && !api.grabbing && !L && circleLimit(camera.position, t)) moved = true; // (grab-orbit trims its own steps)
    if (!free && !L && hard(camera.position, t)) moved = true; // WS-32: hard limits on every path, also mid-flight and mid-grab
    // WS-43: looking at ground further than maxReach (a camera too high for its tilt: an old &cam= pose, a pinch on a
    // slope): glide forward along the view, exponential far out, at least 60 m/s near the limit, never past it
    if (Number.isFinite(reach) && reach > api.maxReach() + 0.5 && !api.grabbing) {
      const h = Math.min(dt, 0.1), ex = reach - api.maxReach(), k = 1 - Math.exp(-stiff * h);
      fwd.copy(t).sub(camera.position).normalize();
      camera.position.addScaledVector(fwd, Math.min(ex, Math.max(ex * k, 60 * h)));
      api.seat(camera.position, t);
      moved = true;
    }
    if (heightAt && api.clearance > 0) { // never under (or skimming) the terrain
      const g = heightAt(camera.position.x, camera.position.z) + api.clearance;
      if (camera.position.y < g) { camera.position.y = g; moved = true; }
    }
    if (moved) camera.lookAt(t);
  };
  api.overshoot = over; // WS-43: the pan drag's soft edge
  api.circleOk = (p) => api.loose || !circle || Math.hypot(p.x - circle.c[0], p.z - circle.c[1]) <= circle.r + 0.01;
  api.clearance = clear;
  api.nearest = (x, z) => (api.loose ? { x, z, d: -1 } : region(x, z));
  api.info = () => ({ hull: poly.map((q) => q.map(Math.round)), circle: circle && { c: circle.c.map(Math.round), r: Math.round(circle.r) }, maxDistance: controls.maxDistance, minElevation: 90 - THREE.MathUtils.radToDeg(controls.maxPolarAngle), maxElevation: 90 - THREE.MathUtils.radToDeg(controls.minPolarAngle), groundClearance: clear });
  return api;
}

/**
 * WS-17 (round 4c follow-up): grab-point orbit. The user feedback: "wherever I grab from is not where it orbits
 * from ... if I grab a certain point, it should always rotate around that point". A rotate drag (right button,
 * Ctrl/Shift/Cmd + left, two-finger touch) picks the world point under the cursor (scene -> ground plane ->
 * current target) and rotates the camera AND the target rigidly about that pivot for the whole drag: yaw about
 * the vertical through the pivot, pitch about the horizontal axis through it. A rigid rotation without roll keeps
 * the pivot exactly under the cursor (lookAt with up = +y reproduces the rotated view), so nothing jumps.
 * MapControls' own rotation is turned off (WS-43: its pan and pinch too, see createGrabPan and pinch()). Limits are respected by trimming the step
 * (elevation range, camera circle, ground clearance), never by moving the camera after the fact; the pan rubber
 * band waits until the drag ends (bounds.grabbing).
 * opts: { camera, controls, canvas, bounds (createCameraBounds api or null), pickPoint(clientX, clientY) -> Vector3|null
 *         (the pivot: the building, tree or ground under the cursor; a miss (the sky, a far hill) on the cursor ray at
 *         most navmath pivotCap(height above the ground) along it, so the camera never swings round a point kilometres away),
 *         heightAt(x, z), speed (default 1 = MapControls' rotate speed; navmath orbitRate: calmer for a lens narrower
 *         than the map's own), dot (pivot marker, default true) }
 * Film (bounds.loose): no orbit; the camera turns where it stands (navmath freeLook, lookRate), straight down to nearly up.
 */
// WS-43: a drag that starts on a label or badge (they select on click) must not also count as a click on it. The
// label follows the map, so it is still under the cursor at release; pointer capture alone did not keep the click
// off it every time. Returns mark(): call it when a drag that travelled more than a tap ends. The canvas keeps its
// own 6 px tap test (main.js), so clicks on it are left alone.
function clickGuard(surfaces) {
  let until = 0;
  addEventListener('click', (e) => {
    if (performance.now() < until && surfaces.some((s) => s && s.contains(e.target))) { e.stopPropagation(); e.preventDefault(); }
    until = 0;
  }, true);
  return () => { until = performance.now() + 400; };
}
const TAP = 6; // px: the canvas' tap threshold (main.js)
const LOOK_TURN = Math.PI; // rad per screen height: Game mode's look around (half a turn a screen; the map's orbit is a whole one)
// ---- mobile gestures (begin, 2026-10-19, user: "panning is very hard ... it's actually inverted", "people rotate with
// their 2 thumbs and now it's like going the opposite way"). A finger wobbles more than a mouse: a touch counts as a
// drag only past TOUCH_TAP px (main.js uses the same number for its tap test), and the two-finger gesture works like the
// phone map apps people know (Google Maps, Apple Maps): see createGrabOrbit's touch mode.
export const TOUCH_TAP = 10;
// soft edge while a drag moves the map: the target eases out to at most `overshoot` m past the pan hull (the rubber band)
const dlEdge = new THREE.Vector3();
function softEdge(bounds, camera, controls) {
  if (!bounds) return;
  const t = controls.target, n = bounds.nearest(t.x, t.z);
  if (n.d <= 0) return;
  const o = bounds.overshoot ?? 40, back = n.d - o * (1 - Math.exp(-n.d / o));
  dlEdge.set((n.x - t.x) / n.d * back, 0, (n.z - t.z) / n.d * back);
  t.add(dlEdge); camera.position.add(dlEdge);
}
// ---- mobile gestures (end)
// Lift (owner 2026-10-27: "the camera wouldn't move, it would just change the actual current height"): camera and target
// rise or sink together by m metres, so the view keeps its direction (the bounds seat the target on the ground again).
// Shift + wheel / two-finger swipe (main.js) and three fingers up or down (createGrabOrbit). Never closer than 3 m (or the
// clearance) over the ground; a rise is trimmed where the view would reach too far (bounds.legal), as the zoom-out is.
const liftC = new THREE.Vector3(), liftT = new THREE.Vector3();
export function liftView({ camera, controls, bounds, heightAt }, m) {
  if (!m || !controls.enabled) return;
  const c = camera.position, t = controls.target;
  if (heightAt) m = Math.max(m, heightAt(c.x, c.z) + Math.max(bounds?.clearance ?? 0, 3) - c.y);
  const ok = (k) => { liftC.copy(c).y += m * k; liftT.copy(t).y += m * k; return !bounds?.legal || bounds.loose || bounds.legal(liftC, liftT); };
  if (m > 0 && !ok(1)) { let lo = 0, hi = 1; for (let i = 0; i < 8; i++) { const k = (lo + hi) / 2; if (ok(k)) lo = k; else hi = k; } m *= lo; }
  if (Math.abs(m) < 1e-3) return;
  c.y += m; t.y += m;
  bounds?.seat?.(c, t);
}
// Walking (2026-10-31, owner: the arrow keys "very glitchy", and Game mode's W A S D): camera and target move (dx, dz) m
// over the ground and follow the terrain under the camera, so its height above the ground stays; past the pan hull the
// soft edge of a drag (the rubber band). Held keys call it once a frame (main.js stepKeys).
export function walkView({ camera, controls, bounds, heightAt }, dx, dz) {
  if ((!dx && !dz) || !controls.enabled) return;
  const c = camera.position, t = controls.target;
  const g0 = heightAt ? heightAt(c.x, c.z) : NaN, g1 = heightAt ? heightAt(c.x + dx, c.z + dz) : NaN;
  const dy = Number.isFinite(g0) && Number.isFinite(g1) ? g1 - g0 : 0;
  c.x += dx; c.y += dy; c.z += dz; t.x += dx; t.y += dy; t.z += dz;
  softEdge(bounds, camera, controls);
}
// Film (bounds.loose): the horizontal heading [x, z] of the view as it stands; straight down or up (no heading left)
// the picture's top, turned round when looking up, so a top-down shot still tilts and moves the way the picture shows
const lookUp = new THREE.Vector3();
function headOf(camera, controls) {
  const d = lookUp.copy(controls.target).sub(camera.position);
  if (d.x * d.x + d.z * d.z > 1e-10 * d.lengthSq()) return [d.x, d.z];
  const s = d.y < 0 ? 1 : -1;
  lookUp.set(0, 1, 0).applyQuaternion(camera.quaternion);
  return [lookUp.x * s, lookUp.z * s];
}
// the drag plane and its maths, shared by the one-finger pan (createGrabPan) and the two-finger pan (createGrabOrbit)
function planeTools(camera, controls, canvas, heightAt) {
  const ray = new THREE.Vector3(), m1 = new THREE.Vector3(), m2 = new THREE.Vector3();
  const rayAt = (cx, cy, out) => { // the unit cursor ray from the camera as it stands
    const r = canvas.getBoundingClientRect();
    camera.updateMatrixWorld();
    return out.set(((cx - r.left) / r.width) * 2 - 1, -((cy - r.top) / r.height) * 2 + 1, 0.5).unproject(camera).sub(camera.position).normalize();
  };
  const depression = (v) => Math.atan2(-v.y, Math.hypot(v.x, v.z)); // radians under the horizontal
  // the drag plane from the pose now: through the grabbed height, at least PLANE_K of the camera's height under it
  const planeOf = (anchorY) => {
    const c0 = camera.position.clone(), t0 = controls.target.clone();
    const g = heightAt ? heightAt(c0.x, c0.z) : t0.y, hag = heightAbove(c0.y, g);
    const h = Math.min(anchorY, c0.y - Math.max(2, PLANE_K * (c0.y - g))), H = c0.y - h;
    return { c0, t0, hag, h, H, R: Math.max(H, Math.min(1.5 * c0.distanceTo(t0), missDist(hag))) };
  };
  const hitAt = (F, cx, cy, out) => { // cursor ray from the plane's camera (F.c0, where the camera stands) -> the drag plane
    rayAt(cx, cy, ray);
    const hl = Math.hypot(ray.x, ray.z);
    if (hl < 1e-6) return out.set(F.c0.x, F.h, F.c0.z); // straight down
    const r = planeRange(depression(ray), F.H, F.R) / hl;
    return out.set(F.c0.x + ray.x * r, F.h, F.c0.z + ray.z * r);
  };
  // the metres one pixel moves the plane's point under (x, y) or under the screen centre (the larger): the move clamp's unit
  const mppOf = (F, x, y) => {
    const r = canvas.getBoundingClientRect();
    let m = 0;
    for (const [px, py] of [[x, y], [r.left + r.width / 2, r.top + r.height / 2]]) {
      hitAt(F, px, py, m1);
      m = Math.max(m, hitAt(F, px + 1, py, m2).distanceTo(m1), hitAt(F, px, py + 1, m2).distanceTo(m1));
    }
    return Math.max(m, 1e-3);
  };
  return { rayAt, depression, planeOf, hitAt, mppOf };
}


export function createGrabOrbit({ camera, controls, canvas, bounds = null, pickPoint, heightAt, speed = 1, dot = true, surfaces = [], over = null }) {
  const dragged = clickGuard(surfaces);
  const fov0 = camera.fov; // the map's own lens (orbitRate)
  controls.enableRotate = false; // MapControls ignores right-drag / modifier-left / two-finger rotate; we own rotation
  controls.enableZoom = false; // WS-43: and the two-finger pinch (pinch() below); the wheel has its own handler (main.js)
  const UP = new THREE.Vector3(0, 1, 0), q = new THREE.Quaternion(), v1 = new THREE.Vector3(), v2 = new THREE.Vector3(), ax = new THREE.Vector3();
  const nc = new THREE.Vector3(), nt = new THREE.Vector3();
  const ptrs = new Map(); // pointerId -> { x, y }
  let drag = null; // { pivot, mode: 'mouse' | 'touch', last }
  let marker = null;
  if (dot) {
    marker = document.createElement('div');
    marker.setAttribute('aria-hidden', 'true');
    Object.assign(marker.style, { position: 'fixed', left: '0', top: '0', width: '10px', height: '10px', margin: '-5px 0 0 -5px', borderRadius: '50%',
      background: 'rgba(255,255,255,0.9)', boxShadow: '0 0 0 1.5px rgba(20,28,40,0.55), 0 1px 6px rgba(0,0,0,0.35)', pointerEvents: 'none',
      opacity: '0', transition: 'opacity 0.35s ease', zIndex: '5' });
    document.body.appendChild(marker);
  }
  const elevOf = (c, t) => Math.asin(THREE.MathUtils.clamp((c.y - t.y) / Math.max(c.distanceTo(t), 1e-6), -1, 1));
  const ok = (c, t) => {
    const el = elevOf(c, t), minEl = Math.PI / 2 - controls.maxPolarAngle, maxEl = Math.PI / 2 - controls.minPolarAngle;
    if (el < minEl - 1e-4 || el > maxEl + 1e-4) return false;
    if (bounds && !(bounds.hardOk ? bounds.hardOk(c, t) : bounds.circleOk(c))) return false; // WS-32: + target and distance
    if (heightAt && c.y < heightAt(c.x, c.z) + (bounds?.clearance ?? 2)) return false;
    if (bounds?.reach) { // WS-43: the view may not reach ground beyond maxReach (that is how the camera climbed to 1.1 km)
      const s = bounds.reach(c, t);
      if (Number.isFinite(s) && s > bounds.maxReach() && s > (drag?.reach ?? Infinity) + 0.5) return false; // an old pose past it may only improve
    }
    return true;
  };
  const sc = new THREE.Vector3(), st = new THREE.Vector3(), tc = new THREE.Vector3();
  const shrink = (pivot, k) => { // WS-43: homothety about the pivot: the pivot stays exactly under the cursor
    sc.copy(nc).sub(pivot).multiplyScalar(k).add(pivot); st.copy(nt).sub(pivot).multiplyScalar(k).add(pivot);
  };
  const commit = (c, t) => {
    camera.position.copy(c); controls.target.copy(t);
    bounds?.seat?.(camera.position, controls.target); // WS-43: rigid pitch about an off-centre pivot lifts or sinks the target: back onto the ground
    camera.lookAt(controls.target);
    if (drag && bounds?.reach) { const s = bounds.reach(camera.position, controls.target); drag.reach = Number.isFinite(s) ? Math.max(s, bounds.maxReach()) : Infinity; }
  };
  // rotate camera + target rigidly about the pivot: yaw (rad) about +y, then pitch (rad, + = camera goes up)
  const rotated = (pivot, yaw, pitch, outC, outT) => {
    outC.copy(camera.position).sub(pivot); outT.copy(controls.target).sub(pivot);
    if (yaw) { q.setFromAxisAngle(UP, yaw); outC.applyQuaternion(q); outT.applyQuaternion(q); }
    if (pitch) {
      v1.copy(outT).sub(outC).setY(0); // horizontal view direction
      if (v1.lengthSq() > 1e-8) { ax.crossVectors(UP, v1.normalize()); q.setFromAxisAngle(ax, pitch); outC.applyQuaternion(q); outT.applyQuaternion(q); }
    }
    outC.add(pivot); outT.add(pivot);
  };
  // a turn with no tilt in it climbs over rising ground: camera and target rise together onto the ground clearance (as
  // liftView, so the view keeps its direction). Close to the ground the camera stands exactly on the clearance (bounds.apply
  // puts it there), and a turn step a millimetre uphill was refused, about the pivot and about the target alike: the turn
  // stopped one way, and the other way after about one round (owner 2026-10-29, looking up next to a cabin)
  const climb = (c, t) => {
    const m = heightAt ? heightAt(c.x, c.z) + (bounds?.clearance ?? 2) - c.y : 0;
    if (m > 0) { c.y += m; t.y += m; }
  };
  // looking up (headlook.js): over the last BLEND above the lowest tilt the tilt slows to the look-up's rate (over.rate),
  // still round the grabbed point (owner 2026-10-27: sliding the turning point to the camera there lost the cabin he grabbed)
  const BLEND = (12 * Math.PI) / 180;
  const step = (yaw, pitch) => {
    if (!drag) return;
    if (over?.active()) { // WS-47: looking up owns the gesture while it is on (skyview.js); headlook.js (over.tilt) takes only the
      over.feed(yaw, pitch * (over.rate ?? 1)); // tilt (at its own rate): the turn goes on below, round the place in view, as on the map
      if (!over.tilt || !yaw) return;
      pitch = 0; // (about the grabbed point's vertical: in the sky it is pickPoint's capped point, a place in front)
    }
    // trim the pitch to the elevation range first (exact), then take the largest allowed part of the step
    const el = elevOf(camera.position, controls.target), minEl = Math.PI / 2 - controls.maxPolarAngle, maxEl = Math.PI / 2 - controls.minPolarAngle;
    const w = over?.tilt && !over.held() ? 1 - THREE.MathUtils.smoothstep(el, minEl, minEl + BLEND) : 0;
    pitch *= 1 + ((over?.rate ?? 1) - 1) * w; // (headlook.js, owner 2026-10-27 "the angle is changing super freely"): calmer towards the limit, the look-up's rate at it
    const want = pitch;
    pitch = THREE.MathUtils.clamp(pitch, Math.min(0, minEl - el), Math.max(0, maxEl - el));
    if (bounds?.loose) { // VIS-265 Film's free look (owner 2026-10-25): the camera turns where it stands at any angle, from straight down to nearly straight up (no sky view, no orbit); drag right = look right, down = look down
      const s = drag.turnK ?? 1, c = camera.position, [x, y, z] = freeLook(nt.copy(controls.target).sub(c).toArray(), yaw * s, want * s,
        controls.minPolarAngle - Math.PI / 2 + 1e-3, controls.maxPolarAngle - Math.PI / 2 - 1e-3, headOf(camera, controls));
      commit(c, nt.set(c.x + x, c.y + y, c.z + z));
      return;
    }
    if (over && !over.active()) { // WS-47: a tilt down that gains nothing (trimmed, or no lower than a step ago), pushed on ~7 px, goes on there
      // over.strict (camera.sky.push, user 2026-10-15 "a tiny bit of movement activates it"): only a mostly vertical push
      // adds up and it is over.push rad long. The limit is wherever the tilt stops going down: the lowest elevation, or
      // close up the ground clearance (next to a cabin it holds the camera at ~18°, user: "very close up, it's not
      // possible"); a step that still tilts down by a quarter of what was asked is progress
      const down = want < -1e-6, floor = want < pitch - 1e-6 && el - minEl < 2e-3;
      const stuck = drag.el != null && el > drag.el - (over.strict ? 0.25 * (drag.want || 0) : 1e-5);
      const held = down && (floor || stuck);
      if (down) { drag.el = el; drag.want = -want; } else if (want > 1e-6) drag.el = null;
      // map controls (owner 2026-10-25, "the tilt doesn't work" on a phone: the push that reached the limit went on into
      // the sky): a gesture that tilted down 2 degrees+ stops at the limit; only a new one, from the limit, looks up
      drag.top = Math.max(drag.top ?? el, el);
      // (headlook.js, owner 2026-10-27: looking up is no mode any more, so the same gesture goes on up after the push: over.seamless)
      const adds = held && (!over.strict || Math.abs(yaw) < -want * 2) && (over.seamless || el > drag.top - 0.035);
      drag.push = adds ? (drag.push || 0) - want : held ? drag.push || 0 : down || want > 1e-6 ? 0 : drag.push || 0;
      if (held && drag.push > (over.push ?? 0.06) && over.feed(0, want)) { drag.push = 0; if (!over.tilt || !yaw) return; pitch = 0; } // (headlook.js: the turn of this step goes on)
    }
    if (Math.abs(pitch) < 1e-7) pitch = 0; // WS-43: a clamped pitch of -1e-17 made a no-op step 'succeed' and the yaw never ran
    // WS-43: a far pivot (the sky, a far hill: the cursor ray at the orbit distance) at the edge of the limits can
    // refuse every step; then the camera turns about the target (the screen centre) instead of stopping
    for (const pv of drag.look ? [drag.pivot] : [drag.pivot, tc.copy(controls.target)]) { // Game mode's look never swings the camera round the target: at a limit it stops
      for (const [y, p] of [[yaw, pitch], [yaw, 0], [0, pitch], [yaw / 2, pitch / 2]]) {
        if (!y && !p) continue;
        rotated(pv, y, p, nc, nt);
        if (!p) climb(nc, nt); // (a tilt still stops at the clearance: there the look-up's push starts, headlook.js)
        if (ok(nc, nt)) { commit(nc, nt); return; }
        // WS-43: refused (camera circle near an off-site zone, reach, distance): move in towards the pivot as far as
        // needed instead of stopping the rotation (round 6: "at the ice karting it gets stuck")
        // (the allowed scales are an interval: too far out breaks the reach or the circle, too far in the ground
        // clearance; walk in until one fits, then bisect back out to the largest)
        const r = nc.distanceTo(pv), kMin = Math.min(1, Math.max(0.3, (controls.minDistance + 2) / Math.max(r, 1e-6)));
        let lo = 0, hi = 1;
        for (const k of [0.97, 0.9, 0.8, 0.65, 0.5, 0.3]) { if (k < kMin) break; shrink(pv, k); if (ok(sc, st)) { lo = k; break; } hi = k; }
        if (!lo) continue;
        for (let i = 0; i < 8; i++) { const m = (lo + hi) / 2; shrink(pv, m); if (ok(sc, st)) lo = m; else hi = m; }
        shrink(pv, lo); commit(sc, st); return;
      }
    }
  };
  // WS-43: the two-finger pinch is a homothety of camera + target about the grabbed point (what the wheel zoom does
  // about the cursor point), so the point stays under the fingers while they twist, tilt and pinch at once.
  // MapControls' own pinch dollied about the target on every pointer event, in between the orbit steps about the
  // pivot: the two do not commute, and with the target far out on the ground at a low angle the pivot slid off the
  // fingers (8 px in the QA gesture). k < 1 = closer; a step past a limit keeps its largest allowed part.
  const pinch = (k) => {
    if (!drag || !(k > 0) || Math.abs(k - 1) < 1e-5) return;
    const d = Math.max(camera.position.distanceTo(controls.target), 1e-6);
    k = THREE.MathUtils.clamp(k, Math.min(1, controls.minDistance / d), Math.max(1, controls.maxDistance / d));
    nc.copy(camera.position); nt.copy(controls.target);
    shrink(drag.pivot, k);
    if (!ok(sc, st)) {
      let lo = 1, hi = k;
      for (let i = 0; i < 8; i++) { const m = (lo + hi) / 2; shrink(drag.pivot, m); if (ok(sc, st)) lo = m; else hi = m; }
      if (lo === 1) return;
      shrink(drag.pivot, lo);
    }
    commit(sc, st);
  };
  const showDot = (on) => { if (marker) marker.style.opacity = on && !bounds?.loose ? '0.85' : '0'; }; // (Film turns where it stands: no pivot)
  const start = (x, y, mode) => {
    drag = { pivot: pickPoint(x, y) || controls.target.clone(), mode };
    if (bounds?.reach) { const s = bounds.reach(camera.position, controls.target); drag.reach = Number.isFinite(s) ? Math.max(s, bounds.maxReach()) : Infinity; } // WS-43
    if (bounds) bounds.grabbing = true;
    showDot(true);
  };
  const end = () => { if (drag?.far > TAP) dragged(); drag = null; if (bounds) bounds.grabbing = false; showDot(false); };
  // Game mode (2026-10-31, owner: "turning around and tilting would work just with a left click and then dragging"): a
  // mouse, pen or trackpad drag with either button turns the view, the left one where the camera stands (down); touch stays
  let game = false;
  const isRotate = (e) => (game && e.pointerType !== 'touch' && (e.button === 0 || e.button === 2)) || (e.pointerType === 'mouse' && (e.button === 2 || (e.button === 0 && (e.ctrlKey || e.metaKey || e.shiftKey))));
  // ---- mobile gestures (begin): two fingers, as in the phone map apps. The fingers' midpoint grabs a ground point
  // (the pivot) and the map stays glued under the fingers: spreading them zooms in about it, twisting them turns the
  // map WITH the fingers, moving them both pans. Pushing both fingers up or down together tilts instead. What the
  // gesture is, and when zoom and twist join in, is navmath twoFingers (owner 2026-10-25: "the tilt often doesn't
  // start", "turning always also zooms"): decided once both fingers moved 2 px (a lagging one gets 100 ms), once a
  // frame (the fingers' moves arrive one by one: deciding on half-updated pairs broke the tilt).
  // the pan: the drag plane through the pivot, at least PLANE_K heights under the camera (as the one-finger pan)
  const { planeOf, hitAt, mppOf } = planeTools(camera, controls, canvas, heightAt);
  const startTouch = () => {
    const [a, b] = ptrs.values(), x = (a.x + b.x) / 2, y = (a.y + b.y) / 2;
    start(x, y, 'touch');
    twoFingers(drag.s = {}, a, b);
    drag.mpp = mppOf(planeOf(drag.pivot.y), x, y) / camera.position.distanceTo(drag.pivot);
  };
  const touchMove = (now) => {
    raf = 0;
    if (!drag?.s || ptrs.size < 2) return;
    const [a, b] = ptrs.values(), S = drag.s, lock = twoFingers(S, a, b, now), c = camera.position, [x0, y0, x, y] = S.m || [];
    if (lock === 1) step(0, (y - y0) * (bounds?.loose ? lookRate(camera.fov, canvas.clientHeight || innerHeight) : (Math.PI / (canvas.clientHeight || innerHeight)) * 1.8 * speed)); // (Film: its free look's rate) the tilt rate: 1.8 pi a screen height (~0.4 degrees a pixel on a phone; Mapbox GL 0.5; was 1.25). Fingers down = camera up (flatter view), up = towards the horizon
    else if (lock) {
      if (S.da) step(S.da, 0); // + = the map turns the way the fingers turn (screen y points down)
      pinch(S.k); // fingers apart = closer, about the pivot
      if (over?.active()) return;
      // the pivot follows the fingers' midpoint: the plane under it moved by the midpoint's move, each move at most
      // MOVE_K x the start's metres per pixel (scaled with the zoom since), so a move towards the horizon never flings
      const F = planeOf(drag.pivot.y);
      hitAt(F, x0, y0, nc); hitAt(F, x, y, nt);
      const [sx, sz] = clampStep(nc.x - nt.x, nc.z - nt.z, MOVE_K * drag.mpp * c.distanceTo(drag.pivot) * Math.max(1, Math.hypot(x - x0, y - y0)));
      c.x += sx; c.z += sz; controls.target.x += sx; controls.target.z += sz;
      softEdge(bounds, camera, controls);
    }
  };
  let raf = 0;
  // three fingers up or down: the camera rises or sinks where it stands (liftView); a screen height = 1.5 heights
  let lift3 = null, rafLift = 0;
  const meanY = () => { let s = 0; for (const p of ptrs.values()) s += p.y; return s / ptrs.size; };
  const liftMove = () => {
    rafLift = 0;
    if (lift3 == null) return;
    const y = meanY(), c = camera.position, hag = Math.max(5, heightAbove(c.y, heightAt ? heightAt(c.x, c.z) : controls.target.y));
    liftView({ camera, controls, bounds, heightAt }, ((lift3 - y) / (canvas.clientHeight || innerHeight)) * 1.5 * hag); // fingers up = camera up
    lift3 = y;
  };
  // ---- mobile gestures (end)
  const down = (e) => {
    ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (!controls.enabled) return;
    if (isRotate(e)) {
      start(e.clientX, e.clientY, 'mouse'); drag.last = { x: e.clientX, y: e.clientY }; drag.x0 = e.clientX; drag.y0 = e.clientY;
      // Game mode: the left button looks around where the camera stands (no pivot dot); the right one (a trackpad's
      // two-finger press) still turns about the grabbed place, as in Map mode (owner 2026-10-31: keep rotating around a building)
      if (game && e.button === 0) { drag.look = true; drag.pivot.copy(camera.position); showDot(false); }
    }
    else if (e.pointerType === 'touch' && ptrs.size === 2) startTouch();
    else if (e.pointerType === 'touch' && ptrs.size === 3) { if (drag) end(); lift3 = meanY(); }
  };
  // WS-43: a drag may start on a label or badge too (surfaces); moves and releases are followed on the window
  for (const el of [canvas, ...surfaces]) el?.addEventListener('pointerdown', down);
  addEventListener('pointermove', (e) => {
    if (ptrs.has(e.pointerId)) ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (lift3 != null && ptrs.has(e.pointerId)) { rafLift ||= requestAnimationFrame(liftMove); return; }
    if (!drag) return;
    const h = canvas.clientHeight || innerHeight, k = drag.look ? (LOOK_TURN / h) * speed : orbitRate(h, camera.fov, fov0, speed); // map controls: a lens narrower than the map's own turns calmer
    if (drag.mode === 'mouse') {
      if (!ptrs.has(e.pointerId)) return;
      if (drag.look) drag.pivot.copy(camera.position); // (the clearance may have lifted the camera)
      drag.turnK = lookRate(camera.fov, h) / k; // Film: its free look follows the pointer (navmath lookRate)
      const dx = e.clientX - drag.last.x, dy = e.clientY - drag.last.y;
      drag.far = Math.max(drag.far || 0, Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0)); // WS-43: more than a tap (clickGuard)
      drag.last = { x: e.clientX, y: e.clientY };
      step(-dx * k, dy * k); // same feel as MapControls: drag right = orbit left, drag down = camera up (Game mode's look: right = look right, up = look up)
    } else if (ptrs.has(e.pointerId)) raf ||= requestAnimationFrame(touchMove); // mobile gestures: once a frame, both fingers' moves in
  });
  const up = (e) => {
    ptrs.delete(e.pointerId);
    if (ptrs.size < 3) lift3 = null; // the lift ends with the third finger (the two left do nothing until placed again)
    if (drag && (drag.mode === 'mouse' || ptrs.size < 2)) end();
    else if (drag) drag.s.p = 0; // mobile gestures: one of three fingers lifted: the two left go on, the gesture's kind and pivot kept
  };
  addEventListener('pointerup', up, true); // WS-43: window (see down)
  addEventListener('pointercancel', up, true);
  // Shift + arrows orbit (main.js keyboard block) about the target: first slide the target along the view ray onto
  // what is under the screen centre (no visual change), so the keyboard orbit also turns about the point in view.
  addEventListener('keydown', (e) => {
    if (!e.shiftKey || !e.key.startsWith('Arrow') || e.metaKey || e.ctrlKey || e.altKey || e.target.closest?.('input, textarea, select') || document.querySelector('#interior.on')) return;
    const r = canvas.getBoundingClientRect(), hit = pickPoint(r.left + r.width / 2, r.top + r.height / 2);
    if (!hit) return;
    const d = camera.position.distanceTo(hit);
    if (d >= controls.minDistance && d <= controls.maxDistance) controls.target.copy(hit);
  }, true);
  return {
    get active() { return !!drag && drag.mode !== 'key'; },
    get pivot() { return drag?.pivot || null; },
    set game(v) { game = !!v; },
    /**
     * Held keys (main.js stepKeys): one frame's turn, yaw and pitch in rad as a drag's step (limits, looking up and the
     * bounds included). About the target (Map mode's Shift + arrows: camerabounds' keydown below put it under the screen
     * centre), or `inPlace` where the camera stands (Game mode). keyEnd() once the keys are let go.
     */
    keyTurn(yaw, pitch, inPlace = false) {
      if (!controls.enabled || (drag && drag.mode !== 'key')) return;
      if (!drag) {
        drag = { pivot: controls.target.clone(), mode: 'key' };
        if (bounds?.reach) { const s = bounds.reach(camera.position, controls.target); drag.reach = Number.isFinite(s) ? Math.max(s, bounds.maxReach()) : Infinity; }
        if (bounds) bounds.grabbing = true;
      }
      drag.look = inPlace;
      if (inPlace) drag.pivot.copy(camera.position);
      step(yaw, pitch);
    },
    keyEnd() { if (drag?.mode === 'key') { drag = null; if (bounds) bounds.grabbing = false; } },
    frame() { // keep the marker on the pivot (it stays under the cursor; it also follows a pinch while dragging)
      if (!marker || !drag) return;
      v2.copy(drag.pivot).project(camera);
      const r = canvas.getBoundingClientRect();
      marker.style.transform = `translate(${r.left + (v2.x + 1) / 2 * r.width}px, ${r.top + (1 - v2.y) / 2 * r.height}px)`;
    },
    dispose() { marker?.remove(); },
  };
}

/**
 * WS-43 (user, 2026-10-12: "if I grab the restaurant and try to move there, I need to drag like 15 times"):
 * ground-anchored pan, replacing MapControls' pan (which scales with the camera-target distance, so it crawled
 * whenever the target was not the ground point at the screen centre, and drifted off the cursor even when it was).
 * A left drag (mouse without modifiers, pen, one finger) grabs the point under the cursor and moves camera + target
 * so that it stays exactly under the cursor: the cursor ray from the drag-start camera meets the horizontal plane
 * through the grabbed point. A drag may start on a label or badge (surfaces); once it moves it is captured by the
 * canvas, so the label's click (select) only fires for a tap. Past the pan hull the drag meets a soft rubber band
 * (never more than the overshoot); on release the motion glides out (inertia, like MapControls' damping) and the
 * bounds relax the target back. The two-finger pinch belongs to the grab orbit (createGrabOrbit pinch()); MapControls
 * only eases and clamps now.
 * Map controls (2026-10-25, owner: "one grab sends the camera somewhere far away"; rates in navmath.js): the drag
 * plane stays at least PLANE_K of the camera's height under it (a roof at eye level made one pixel worth hundreds of
 * metres); the grabbed point follows the cursor exactly out to 1.5 orbit distances (at most navmath missDist), past
 * that at a steady rate (navmath planeRange), and its depth stops GRAZE above the horizon; each move is clamped to
 * MOVE_K x the drag start's metres per pixel. A grab within GRAZE of the horizon, or in the sky, turns the view where
 * the camera stands (the picture follows the pointer, fov / h a pixel, inside the tilt limits): it never translates.
 * The release glide is the pointer's screen speed at the start's metres per pixel, at most 1.5 heights a second,
 * and none after a clamped move. nudge(dx, dy): a trackpad's two-finger swipe (main.js wheel, already softened and
 * railed by navmath createSwipe), (dx, dy) px at the screen centre's sideways metres per pixel both ways, on the same
 * plane, with the same turn and soft edge.
 * opts: { camera, controls, canvas, bounds, pickPoint(clientX, clientY, 'pan') -> the visible surface under the
 *         cursor (Vector3) or null (the sky, or further than navmath missDist), heightAt(x, z),
 *         surfaces ([elements] besides the canvas), onStart() (called when a pan starts), inertia (1/s, default 4.6),
 *         looking() (headlook.js: looking up, where a grab in the sky moves the camera as in Film) }
 */
export function createGrabPan({ camera, controls, canvas, bounds = null, pickPoint, heightAt, surfaces = [], onStart = null, inertia = 4.6, looking = () => false }) {
  controls.enablePan = false; // MapControls: left drag / one finger do nothing now (stays off: two pans under one drag twitch)
  let enabled = true; // this pan's own switch (camera.rule)
  let game = false; // Game mode: a mouse, pen or trackpad drag looks around (createGrabOrbit); one finger still pans
  const dragged = clickGuard(surfaces);
  const touches = new Map(), ray = new THREE.Vector3(), q = new THREE.Vector3(), q2 = new THREE.Vector3(), dl = new THREE.Vector3(); // touches: pointerId -> [x, y] (mobile gestures)
  const dir = new THREE.Vector3(), tt = new THREE.Vector3(), { rayAt, depression, planeOf, hitAt, mppOf } = planeTools(camera, controls, canvas, heightAt);
  let drag = null; // { id, F (the drag plane), aY, P, mpp, turn, c0, t0, x0, y0, lx, ly, ox, oz, slop, moved, far, hist, clampedAt }
  let glide = null; // { v } world m/s
  // the view turns where the camera stands, the picture following the pointer by (dx, dy) px, inside the tilt limits
  // (MapControls would undo the rest); on the visitor map only a turn that keeps the bounds (bounds.legal)
  const turnBy = (dx, dy) => {
    const k = turnRate(camera.fov, canvas.clientHeight || innerHeight), c = camera.position;
    dir.copy(controls.target).sub(c);
    const L = Math.max(dir.length(), 1e-6), yaw = Math.atan2(dir.x, dir.z), p0 = Math.asin(THREE.MathUtils.clamp(dir.y / L, -1, 1));
    const lo = controls.minPolarAngle - Math.PI / 2 + 1e-3, hi = controls.maxPolarAngle - Math.PI / 2 - 1e-3;
    const p1 = THREE.MathUtils.clamp(p0 + dy * k, Math.min(lo, p0), Math.max(hi, p0)); // drag down = look up
    for (const [y, p] of [[yaw + dx * k, p1], [yaw + dx * k, p0], [yaw, p1]]) { // drag right = turn left
      tt.set(Math.sin(y) * Math.cos(p), Math.sin(p), Math.cos(y) * Math.cos(p)).multiplyScalar(L).add(c);
      if (bounds && !bounds.loose) { bounds.seat?.(c, tt); if (bounds.legal && !bounds.legal(c, tt)) continue; }
      controls.target.copy(tt); camera.lookAt(tt);
      return true;
    }
    return false;
  };
  // Film (bounds.loose; owner 2026-10-25: "still tilt the camera up and still move around position-wise"): where the
  // map turns the view (a grab in the sky or at the horizon, a swipe there) the camera moves like a drone instead, at its
  // height, the view kept: the picture follows the pointer, (dx, dy) px at navmath flyRate
  const fly = (dx, dy) => {
    const c = camera.position, [hx, hz] = headOf(camera, controls), l = Math.hypot(hx, hz) || 1;
    const m = flyRate(heightAbove(c.y, heightAt ? heightAt(c.x, c.z) : NaN), camera.fov, canvas.clientHeight || innerHeight) / l;
    const sx = (dy * hx + dx * hz) * m, sz = (dy * hz - dx * hx) * m; // drag down = forward, right = to the left
    c.x += sx; c.z += sz; controls.target.x += sx; controls.target.z += sz;
  };
  const end = (keepGlide) => {
    if (!drag) return;
    if (keepGlide && drag.moved && !drag.turn && !drag.two && drag.hist.length > 1) { // (none for the finger left after a pinch or a twist)
      const now = performance.now(), last = drag.hist[drag.hist.length - 1];
      const first = drag.hist.find((s) => now - s.t <= 100) || last;
      const dt = (last.t - first.t) / 1000;
      const clamped = now - (drag.clampedAt || 0) < 150; // the last moves met the clamp (the horizon): no fling
      if (!clamped && now - last.t < 60 && dt > 0.008) {
        const v = new THREE.Vector3(last.wx - first.wx, 0, last.wz - first.wz);
        if (v.lengthSq() > 1e-8) {
          v.setLength(glideSpeed(Math.hypot(last.x - first.x, last.y - first.y) / dt, drag.mpp, drag.F.hag));
          if (v.length() > 0.5) glide = { v };
        }
      }
    }
    if (drag.far > (drag.slop > 3 ? TOUCH_TAP : TAP)) dragged(); // the click that follows is not a tap on the label the drag started on
    drag = null;
    if (bounds) bounds.panning = false;
  };
  const anchor = () => { // the grabbed point on the plane, its metres per pixel; a grab at the horizon or in the sky turns the view
    drag.turn = depression(rayAt(drag.x0, drag.y0, ray)) < GRAZE;
    if (!drag.turn) { drag.P = hitAt(drag.F, drag.x0, drag.y0, drag.P || new THREE.Vector3()); drag.mpp = mppOf(drag.F, drag.x0, drag.y0); }
  };
  const begin = (id, x, y, slop) => {
    const picked = pickPoint(x, y, 'pan'); // what is seen under the cursor; null: the sky or a far hill (the target's height)
    const aY = picked ? picked.y : controls.target.y, F = planeOf(aY);
    drag = { id, F, aY, c0: F.c0, t0: F.t0, x0: x, y0: y, lx: x, ly: y, ox: 0, oz: 0, slop, moved: false, hist: [] };
    anchor();
  };
  const down = (e) => {
    glide = null;
    if (e.pointerType === 'touch') touches.set(e.pointerId, [e.clientX, e.clientY]);
    if (drag && (e.pointerType !== 'touch' || touches.size > 1)) { end(false); return; } // a second finger: pinch / twist
    if (!controls.enabled || !enabled || (game && e.pointerType !== 'touch')) return; // WS-49: camera.rule `focus` = orbit and zoom only
    const primary = e.pointerType === 'touch' ? touches.size === 1 : e.button === 0 && !(e.ctrlKey || e.metaKey || e.shiftKey);
    if (!primary) return;
    // mobile gestures: a finger counts as a drag only past TOUCH_TAP px (a wobbling tap no longer nudges the map); once
    // it moves, the grabbed point catches up under the finger at once, so nothing is lost
    begin(e.pointerId, e.clientX, e.clientY, e.pointerType === 'touch' ? TOUCH_TAP : 3);
  };
  const move = (e) => {
    if (touches.has(e.pointerId)) touches.set(e.pointerId, [e.clientX, e.clientY]);
    if (!drag || e.pointerId !== drag.id) return;
    if (!drag.moved) {
      if (Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0) < drag.slop) return;
      drag.moved = true;
      if (bounds) bounds.panning = true;
      onStart?.();
      try { if (!canvas.hasPointerCapture(e.pointerId)) canvas.setPointerCapture(e.pointerId); } catch { /* errors:ignore: the pointer was already released */ }
      // the camera may have moved since the press (a flight that a press on a label does not stop, the bounds
      // relaxing): anchor the drag on the pose it really starts from
      drag.F = planeOf(drag.aY); drag.c0 = drag.F.c0; drag.t0 = drag.F.t0;
      anchor();
    }
    drag.far = Math.max(drag.far || 0, Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0));
    const px = Math.hypot(e.clientX - drag.lx, e.clientY - drag.ly), ddx = e.clientX - drag.lx, ddy = e.clientY - drag.ly;
    drag.lx = e.clientX; drag.ly = e.clientY;
    if (drag.turn) { (bounds?.loose || looking() ? fly : turnBy)(ddx, ddy); return; } // grabbed at the horizon: the view turns (Film, and looking up: the camera flies), nothing glides after
    camera.position.copy(drag.c0); // the ray is cast from the drag-start pose (same orientation all drag long)
    hitAt(drag.F, e.clientX, e.clientY, q);
    const wx = drag.P.x - q.x - drag.ox, wz = drag.P.z - q.z - drag.oz;
    const [sx, sz] = clampStep(wx, wz, MOVE_K * drag.mpp * Math.max(1, px));
    const now = performance.now();
    if (Math.hypot(wx, wz) - Math.hypot(sx, sz) > 1e-6) drag.clampedAt = now;
    drag.ox += sx; drag.oz += sz;
    camera.position.set(drag.c0.x + drag.ox, drag.c0.y, drag.c0.z + drag.oz);
    controls.target.set(drag.t0.x + drag.ox, drag.t0.y, drag.t0.z + drag.oz);
    softEdge(bounds, camera, controls);
    drag.hist.push({ t: now, x: e.clientX, y: e.clientY, wx: drag.ox, wz: drag.oz });
    while (drag.hist.length > 2 && now - drag.hist[0].t > 150) drag.hist.shift();
  };
  const up = (e) => {
    const was = touches.size;
    touches.delete(e.pointerId);
    if (drag && e.pointerId === drag.id) end(e.type === 'pointerup');
    // mobile gestures: two fingers down to one (after a pinch or a turn): the finger left on the screen pans on, as in
    // the phone map apps (it used to do nothing until it was lifted and put down again). Anchored where it is now, past
    // the usual TOUCH_TAP and with no glide after it (the second finger lifts 20-60 ms after the first: no fling).
    else if (!drag && was === 2 && touches.size === 1 && controls.enabled && enabled) { const [[id, [x, y]]] = touches; begin(id, x, y, TOUCH_TAP); drag.two = 1; }
  };
  for (const el of [canvas, ...surfaces]) {
    el?.addEventListener('pointerdown', down);
    if (el && el !== canvas) el.addEventListener('contextmenu', (e) => e.preventDefault()); // a right-drag orbit may start on a label
  }
  addEventListener('pointermove', move, true);
  addEventListener('pointerup', up, true);
  addEventListener('pointercancel', up, true);
  addEventListener('wheel', () => { glide = null; }, { capture: true, passive: true });
  return {
    get active() { return !!drag?.moved; },
    get gliding() { return !!glide; },
    set enabled(v) { enabled = !!v; if (!v) { end(false); glide = null; } },
    set game(v) { game = !!v; },
    /** Per frame, before the bounds: the release glide. blocked = a flight or the race camera owns the camera. */
    step(dt, blocked = false) {
      if (!glide) return;
      if (blocked || drag) { glide = null; return; }
      const k = Math.exp(-inertia * dt);
      dl.copy(glide.v).multiplyScalar((1 - k) / inertia); // exact integral of v e^(-inertia t) over dt
      glide.v.multiplyScalar(k);
      camera.position.add(dl); controls.target.add(dl);
      if (glide.v.length() < 0.3 || (bounds && bounds.nearest(controls.target.x, controls.target.z).d > 0)) glide = null; // the band takes over
    },
    stop() { glide = null; },
    /** A trackpad's two-finger swipe: the picture moves (dx, dy) px (content follows the fingers). False: panning is off. */
    nudge(dx, dy) {
      if (!enabled || !controls.enabled || drag) return false;
      glide = null;
      if (!dx && !dy) return true;
      const r = canvas.getBoundingClientRect(), x = r.left + r.width / 2, y = r.top + r.height / 2;
      if (depression(rayAt(x, y, ray)) < GRAZE) { (bounds?.loose || looking() ? fly : turnBy)(dx, dy); return true; } // looking at the horizon: the view turns (Film, and looking up: the camera flies)
      // one rate both ways (2026-10-25, "extremely sensitive"): the metres a pixel moves the centre point sideways,
      // forward and back too (the picture's own depth rate is 1 / sin(tilt) of it: 3x at 20 degrees, 6x at 10)
      const F = planeOf(controls.target.y);
      hitAt(F, x, y, q2); hitAt(F, x + 1, y, q).sub(q2); hitAt(F, x, y + 1, dl).sub(q2);
      const m = Math.max(q.length(), 1e-3);
      q.setLength(dx * m); dl.setLength(dy * m);
      if (dl.lengthSq() + q.lengthSq() < 1e-12) return true;
      const sx = -(q.x + dl.x), sz = -(q.z + dl.z);
      camera.position.x += sx; camera.position.z += sz; controls.target.x += sx; controls.target.z += sz;
      softEdge(bounds, camera, controls); // (the rubber band pulls back between the swipe's events)
      return true;
    },
  };
}
